/**
 * Finance vertical — the shipped Extern Labs sprint, expressed as a {@link VerticalPack}.
 *
 * This file is an ADAPTER. It reads the LIVE finance data layer (`finance-discovery/_data/*`) and
 * projects it onto the engine's domain-neutral contract WITHOUT modifying a single finance file. That
 * constraint is not squeamishness: `finance-discovery/` is in production with a causal experiment
 * running on it, so the only safe way to prove the abstraction is to describe the shipped thing from
 * the outside and see what refuses to fit.
 *
 * WHERE THE SHAPES DISAGREE — the honest list of what had to be adapted:
 *
 *   1. TABLE ROWS. `Company.rows` is a `Record<rowKey, number[]>` whose display ORDER and LABELS live
 *      nowhere near it — they are a separate `STATEMENT_ROWS` array in `_data/m3-numbers.ts`, and
 *      which rows are subtotals is `new Set([2, 4])`, hardcoded ROW INDEXES, in two screens. The
 *      contract's `LabsTableRow[]` is ordered and self-describing, so `toRows()` below joins the three
 *      back together. This is the single biggest adaptation, and the one that most justifies the
 *      contract: no non-financial vertical could ever satisfy "rows 2 and 4 are the subtotals".
 *   2. `Company.years` → `periods`; `currency` + `magnitude` → `unit: {prefix, suffix}`; `ticker` →
 *      `subtitle`. The engine formats a cell as `prefix + value + suffix` rather than knowing what
 *      money is.
 *   3. `PracticeCompany` (NovaTech) is a LEANER shape than `Company` — no id, logo, accent, ticker,
 *      tags, and no `currency` at all (the "$" is hardcoded in m3-activity's `fmt`). Those fields are
 *      synthesised here so the practice subject and the real subjects render through one code path.
 *   4. `M2Question.corpSide` → `scoredSide`. Its `leftAnchor`/`rightAnchor` prose is dropped: it is in
 *      the data but no screen has ever rendered it.
 *   5. `Ratio` → `LabsDerivedMetric`, `CellRef.yearIndex` → `periodIndex`, `RATIO_TOLERANCE` →
 *      `m3.tolerance`, and the "%" the screen appends to every option → `m3.unitSuffix`.
 *   6. M1 cards have no stable id — the store keys their answers by array index (`String(card.idx)`).
 *      The contract requires one, so each card gets `m1-1`…`m1-8` in the existing deck order.
 *      `persona: 'alex' | 'sam'` → `pole: 'a' | 'b'`.
 *   7. The axis colours (`#0F4D3A` / `#7C3AED` / `#B8860B`) exist THREE times in finance — as
 *      `CORP_GREEN`/`INVEST_PURPLE` in m2-activity.tsx and again as archetype accents in
 *      archetypes.ts. Here `axis` is the single source and the archetypes read from it.
 *   8. `Archetype.label` / `.banner` / `.bodyClass` have no contract field. Nothing renders them.
 *
 * ANALYTICS: `events` is `FINANCE_DISCOVERY_EVENTS` verbatim. The live experiment — the board, the
 * digest, the significance read — keys off those exact names. Renaming one silently breaks the causal
 * read, so the contract takes each pack's literal names rather than building them from a prefix.
 */

import { TrendingUp } from 'lucide-react';

import { FINANCE_DISCOVERY_EVENTS } from '@/lib/analytics/finance-discovery-events';

import {
  ARCHETYPES,
  type Archetype,
  COMPANIES,
  type Company,
  deriveArchetype,
  type IncomeStatement,
  M1_CARDS,
  M2_QUESTIONS,
  M3_RATIOS,
  M4_GROUPS,
  M4_PICK_ANSWER_KEY,
  type M4AnswerKey,
  NOVATECH,
  type PracticeCompany,
  RATIO_TOLERANCE,
  RECOMMENDATIONS,
  SAMPLE_ANALYSES,
  STATEMENT_ROWS,
  TAKE_SAMPLES,
} from '../../../finance-discovery/_data';
import { ARCHETYPE_PRIORITY_SUBTYPES, FINANCE_CATEGORIES } from '../../../finance-discovery/_data/live-recommendations';
import type {
  LabsAnswerKey,
  LabsArchetype,
  LabsAxis,
  LabsDerivedMetric,
  LabsMindsetQuestion,
  LabsSubject,
  LabsSwipeCard,
  LabsTableRow,
  VerticalPack,
} from '../_engine/types';
import { FINANCE_COPY } from './finance-copy';

/**
 * The axis: FP&A (inside the business) vs Investing (outside it). Every colour below is the hex the
 * shipped screens already paint — green for the operator side, purple for the markets side, gold for
 * the learner fluent in both (THE POLYMATH).
 */
const FINANCE_AXIS: LabsAxis = {
  bothAccent: '#B8860B',
  poles: {
    a: { accent: '#0F4D3A', label: 'FP&A' },
    b: { accent: '#7C3AED', label: 'Investing' },
  },
};

/** Every option on the M3 metric picker is a percentage — the shipped screen appends this literally. */
const METRIC_UNIT_SUFFIX = '%';

/** The two lines an income statement DERIVES rather than reports; the table shades and bolds them. */
const SUBTOTAL_ROWS: ReadonlySet<keyof IncomeStatement> = new Set(['grossProfit', 'opIncome']);

/**
 * Join `STATEMENT_ROWS` (display order + labels) to a statement's values, which are stored keyed
 * rather than ordered. Every finance subject — the practice one and all five real ones — renders the
 * same five lines, so both callers share this.
 */
function toRows(rows: IncomeStatement): readonly LabsTableRow[] {
  return STATEMENT_ROWS.map((row) => ({
    key: row.key,
    label: row.label,
    values: rows[row.key],
    ...(SUBTOTAL_ROWS.has(row.key) ? { emphasis: 'subtotal' as const } : {}),
  }));
}

/** `Company` → `LabsSubject`. The ticker is the subtitle; currency + magnitude become the unit. */
function toSubject(company: Company): LabsSubject {
  return {
    accent: company.accent,
    id: company.id,
    logo: company.logo,
    monogram: company.monogram,
    name: company.name,
    periods: company.years,
    rows: toRows(company.rows),
    subtitle: company.ticker,
    tagline: company.tagline,
    tags: company.tags,
    unit: { prefix: company.currency, suffix: company.magnitude },
  };
}

/**
 * NovaTech is the M3 practice subject, and `PracticeCompany` carries only what the practice table
 * needed: name, chip, years, magnitude, rows. The presentational fields the engine renders for every
 * subject are synthesised — the accent is the FP&A pole's green (the colour the practice screen already
 * uses), the "$" is the currency the shipped screen hardcodes, and there is no ticker so the subtitle
 * is empty. The practice chip ("Practice example") is the tagline.
 */
function toPracticeSubject(practice: PracticeCompany): LabsSubject {
  return {
    accent: FINANCE_AXIS.poles.a.accent,
    id: 'novatech',
    logo: '',
    monogram: practice.name.charAt(0),
    name: practice.name,
    periods: practice.years,
    rows: toRows(practice.rows),
    subtitle: '',
    tagline: practice.chip,
    tags: [],
    unit: { prefix: '$', suffix: practice.magnitude },
  };
}

/** Drop the three fields nothing renders (`label` / `banner` / `bodyClass`). */
function toArchetype(archetype: Archetype): LabsArchetype {
  return {
    accent: archetype.accent,
    body: archetype.body,
    displayName: archetype.displayName,
    id: archetype.id,
    tagline: archetype.tagline,
  };
}

/** Restate the keyed answer key as a plain group → chip map the engine can look up by string. */
function toAnswerKey(key: M4AnswerKey): LabsAnswerKey {
  return { margin: key.margin, revenue: key.revenue, riskOpp: key.riskOpp };
}

const FINANCE_ARCHETYPES: Readonly<Record<string, LabsArchetype>> = {
  corp: toArchetype(ARCHETYPES.corp),
  gen: toArchetype(ARCHETYPES.gen),
  invest: toArchetype(ARCHETYPES.invest),
};

/**
 * The deck, in its shipped order (it deliberately alternates the two poles). Finance keys a card's
 * answer by its array position; the contract wants a stable id, so the position becomes `m1-{n}`.
 * `alex` (FP&A, inside the business) is pole A; `sam` (investing, outside it) is pole B.
 */
const FINANCE_M1_CARDS: readonly LabsSwipeCard[] = M1_CARDS.map((card, i) => ({
  icon: card.icon,
  id: `m1-${i + 1}`,
  pole: card.persona === 'alex' ? 'a' : 'b',
  text: card.text,
}));

/** `corpSide` names the pole; the engine only needs to know WHICH END of the slider it scores. */
const FINANCE_M2_QUESTIONS: readonly LabsMindsetQuestion[] = M2_QUESTIONS.map((question) => ({
  id: question.id,
  leftLabel: question.leftLabel,
  q: question.q,
  rightLabel: question.rightLabel,
  scoredSide: question.corpSide,
}));

const FINANCE_M3_METRICS: readonly LabsDerivedMetric[] = M3_RATIOS.map((ratio) => ({
  correct: ratio.correct,
  formula: ratio.formula,
  key: ratio.key,
  name: ratio.name,
  options: ratio.options,
  period: ratio.period,
  sources: ratio.sources.map((source) => ({ periodIndex: source.yearIndex, row: source.row })),
}));

const FINANCE_M4_SUBJECTS: Readonly<Record<string, LabsSubject>> = Object.fromEntries(
  Object.entries(COMPANIES).map(([id, company]) => [id, toSubject(company)]),
);

const FINANCE_M4_ANSWER_KEY: Readonly<Record<string, LabsAnswerKey>> = Object.fromEntries(
  Object.entries(M4_PICK_ANSWER_KEY).map(([id, key]) => [id, toAnswerKey(key)]),
);

export const FINANCE_PACK: VerticalPack = {
  archetypes: FINANCE_ARCHETYPES,
  axis: FINANCE_AXIS,
  // The career-focus option the shipped finance entry already triggers on (@/lib/career-focus-interest).
  careerFocusArea: 'Finance',
  copy: FINANCE_COPY,
  // Reused, not reimplemented: this is the exact `scoreM2` algorithm the live sprint grades on, and a
  // second copy of it could drift from the archetype every shipped learner has already been given.
  deriveArchetype,
  displayName: 'Finance',
  events: FINANCE_DISCOVERY_EVENTS,
  featureFlag: 'externship_labs_finance_q2_2026',
  m1: { cards: FINANCE_M1_CARDS },
  m2: { questions: FINANCE_M2_QUESTIONS },
  // Matches the LIVE finance rail ("Finance Sprint" / "Finance", trend-up icon). Not mounted yet — the
  // live finance code still owns its own rail; this exists so the adapter satisfies the contract and is
  // ready when finance migrates onto the engine.
  nav: { icon: TrendingUp, label: 'Finance Sprint', mobileLabel: 'Finance' },
  m3: {
    metrics: FINANCE_M3_METRICS,
    subject: toPracticeSubject(NOVATECH),
    tolerance: RATIO_TOLERANCE,
    unitSuffix: METRIC_UNIT_SUFFIX,
  },
  m4: {
    answerKey: FINANCE_M4_ANSWER_KEY,
    groups: M4_GROUPS,
    sampleAnalyses: SAMPLE_ANALYSES,
    subjects: FINANCE_M4_SUBJECTS,
    takeSamples: TAKE_SAMPLES,
  },
  recommendations: {
    archetypePriority: ARCHETYPE_PRIORITY_SUBTYPES,
    categories: FINANCE_CATEGORIES,
    fallback: RECOMMENDATIONS,
  },
  slug: 'finance',
};
